import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { GALLERY } from "@/data/content";

const CATS = ["All", "Estate", "Interiors", "Goats & Grounds", "Gatherings"];

export default function Gallery() {
  const [cat, setCat] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const shown = cat === "All" ? GALLERY : GALLERY.filter((g) => g.cat === cat);

  return (
    <main data-testid="gallery-page">
      <section className="bg-[#1E1610] pb-14 pt-40 text-[#F4F1EA]">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Reveal>
            <Eyebrow light>Gallery</Eyebrow>
            <h1 className="mt-4 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              The estate, in frames.
            </h1>
            <p className="mt-5 max-w-xl text-[#F4F1EA]/70">
              Stone courtyards, fire-lit evenings, wild corners and the locals — a wander through Plas Llechylched.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16 lg:py-24">
        <Reveal className="flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button
              key={c}
              data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setCat(c)}
              className={`rounded-full px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-all duration-300 ${
                cat === c
                  ? "bg-[#5A4632] text-[#F4F1EA]"
                  : "border border-[#5A4632]/25 text-[#5A4632] hover:border-[#B85D43] hover:text-[#B85D43]"
              }`}
            >
              {c}
            </button>
          ))}
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((g, i) => (
            <Reveal key={g.src} delay={(i % 3) * 0.08}>
              <button
                data-testid={`gallery-item-${i}`}
                onClick={() => setLightbox(g)}
                className="group block w-full overflow-hidden rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#B85D43]"
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="mt-2 block text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#7A6A58]">
                  {g.cat}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-[#1E1610]/95 p-4 backdrop-blur-sm"
          >
            <button
              data-testid="gallery-lightbox-close"
              onClick={() => setLightbox(null)}
              aria-label="Close lightbox"
              className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-full border border-[#F4F1EA]/30 text-[#F4F1EA] transition-colors hover:bg-[#B85D43]"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.figure
              initial={{ scale: 0.94, y: 16 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 8 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-5xl"
            >
              <img src={lightbox.src} alt={lightbox.alt} className="max-h-[80vh] w-full rounded-2xl object-contain" />
              <figcaption className="mt-4 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-[#F4F1EA]/60">
                {lightbox.alt}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
